<template>
  <div class="section">
    <div class="toolbar">
      <input
        v-model="search"
        class="search-input"
        placeholder="Поиск по группе..."
      >
      <div class="toolbar-actions">
        <button class="btn-secondary" :disabled="loading" @click="loadGroups">
          {{ loading ? 'Обновляем...' : 'Обновить' }}
        </button>
        <button class="btn-primary" :disabled="loading" @click="startCreate">+ Добавить</button>
      </div>
    </div>

    <div v-if="hasError" class="error-message">Не удалось загрузить группы. Проверьте подключение к ati-publisher.</div>

    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>ID (slug)</th>
            <th>Название</th>
            <th>Контакты</th>
            <th>Регионы отправления</th>
            <th class="col-bool">Резерв?</th>
            <th class="col-bool">Активна</th>
            <th class="col-actions">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && items.length === 0">
            <td :colspan="7" class="empty-cell">Загрузка...</td>
          </tr>
          <tr v-else-if="filteredItems.length === 0">
            <td :colspan="7" class="empty-cell">Групп не найдено</td>
          </tr>
          <tr v-for="item in filteredItems" :key="item.group_id">
            <td class="mono">{{ item.group_id }}</td>
            <td>{{ item.name }}</td>
            <td>
              <div v-if="(item.contact_ids || []).length" class="members-inline">
                <span
                  v-for="contactId in item.contact_ids"
                  :key="contactId"
                  class="member-inline"
                  :class="{ 'member-inactive': accountByContactId(contactId)?.is_active === false }"
                  :title="accountDetails(contactId)"
                >{{ accountDisplayName(contactId) }}</span>
              </div>
              <span v-else>—</span>
            </td>
            <td>
              <div v-if="(item.departure_region_ids || []).length" class="members-inline">
                <span
                  v-for="regionId in item.departure_region_ids.slice(0, 3)"
                  :key="regionId"
                  class="region-inline"
                  :title="`region_id: ${regionId}`"
                >{{ regionDisplayName(regionId) }}</span>
                <span v-if="item.departure_region_ids.length > 3" class="region-more">
                  ещё {{ item.departure_region_ids.length - 3 }}
                </span>
              </div>
              <span v-else>—</span>
            </td>
            <td class="col-bool">{{ item.is_fallback ? '✓' : '—' }}</td>
            <td class="col-bool">
              <span class="badge" :class="item.is_active ? 'badge-on' : 'badge-off'">
                {{ item.is_active ? 'да' : 'нет' }}
              </span>
            </td>
            <td class="col-actions">
              <button class="btn-edit" :disabled="saving || duplicating" @click="startEdit(item)">Изменить</button>
              <button class="btn-copy" :disabled="saving || duplicating" @click="openDuplicate(item)">Дублировать</button>
              <button class="btn-delete" :disabled="saving || duplicating" @click="removeGroup(item)">
                {{ deletingId === item.group_id ? '...' : 'Удалить' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <h2>{{ editingId ? 'Редактировать группу' : 'Новая группа' }}</h2>
        <div v-if="formError" class="error-message">{{ formError }}</div>
        <label class="field">
          <span>Название *</span>
          <input v-model="form.name" class="modal-input" placeholder="Напр. Север">
        </label>
        <label class="field">
          <span>ID (slug) *</span>
          <input v-model="form.group_id" class="modal-input" placeholder="напр. north" :disabled="!!editingId">
        </label>
        <div class="group-editor-grid">
          <group-multi-select
            v-model="form.contact_ids"
            title="Пользователи"
            :options="accountOptions"
            :loading="accountsLoading"
            :error="accountsError"
            placeholder="Поиск по имени, почте или contact_id"
            empty-text="Пользователи не выбраны"
            unknown-label="Контакт"
          />
          <group-multi-select
            v-model="form.departure_region_ids"
            title="Регионы отправления"
            :options="regionOptions"
            :loading="regionsLoading"
            :error="regionsError"
            :server-search="true"
            placeholder="Поиск по названию или region_id"
            empty-text="Региональное правило не задано"
            unknown-label="Регион"
            @search="onRegionsSearch"
          />
        </div>
        <label class="field-inline">
          <input type="checkbox" v-model="form.is_fallback">
          <span>Резервная группа (принимает непривязанные направления)</span>
        </label>
        <label class="field-inline">
          <input type="checkbox" v-model="form.is_active">
          <span>Активна</span>
        </label>
        <div class="modal-actions">
          <button class="btn-cancel" :disabled="saving" @click="closeModal">Отмена</button>
          <button class="btn-save" :disabled="saving" @click="save">
            {{ saving ? '...' : 'Сохранить' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="dupSource" class="modal-overlay" @click.self="closeDuplicate">
      <div class="modal">
        <h2>Дублировать группу</h2>
        <p class="modal-hint">Создать новую группу на основе <b>{{ dupSource.name }}</b> и скопировать в неё все привязки (область «Группа»).</p>
        <div v-if="dupError" class="error-message">{{ dupError }}</div>
        <label class="field">
          <span>Название *</span>
          <input v-model="dupName" class="modal-input" placeholder="Название новой группы">
        </label>
        <label class="field">
          <span>ID (slug) *</span>
          <input v-model="dupSlug" class="modal-input" placeholder="напр. north">
        </label>
        <label class="field-inline">
          <input type="checkbox" v-model="dupIsFallback">
          <span>Резервная группа (принимает непривязанные направления)</span>
        </label>
        <label class="field-inline">
          <input type="checkbox" v-model="dupIsActive">
          <span>Активна</span>
        </label>
        <div v-if="dupResult" class="info-message">{{ dupResult }}</div>
        <div class="modal-actions">
          <button class="btn-cancel" :disabled="duplicating" @click="closeDuplicate">Отмена</button>
          <button class="btn-save" :disabled="duplicating || !dupSlug" @click="confirmDuplicate">
            {{ duplicating ? '...' : 'Создать копию' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { deleteGroup, getAccounts, getBindings, getGroups, searchRegions, upsertBinding, upsertGroup } from '@/backend/distributorRoutingApi'
import GroupMultiSelect from './GroupMultiSelect.vue'

const TRANSLIT_MAP = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh',
  щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
}

function translitSlug(value) {
  return String(value || '')
    .toLowerCase()
    .split('')
    .map((ch) => {
      if (Object.prototype.hasOwnProperty.call(TRANSLIT_MAP, ch)) return TRANSLIT_MAP[ch]
      if (/[a-z0-9]/.test(ch)) return ch
      if (/[\s_-]/.test(ch)) return '-'
      return ''
    })
    .join('')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function noiseToken(len = 4) {
  let token = Math.random().toString(36).slice(2, 2 + len)
  while (token.length < len) token = Math.random().toString(36).slice(2, 2 + len)
  return token
}

export default {
  name: 'GroupsSection',
  components: { GroupMultiSelect },
  setup() {
    const items = ref([])
    const accounts = ref([])
    const regions = ref([])
    const loading = ref(false)
    const accountsLoading = ref(false)
    const regionsLoading = ref(false)
    const saving = ref(false)
    const hasError = ref(false)
    const accountsError = ref(false)
    const regionsError = ref(false)
    const search = ref('')
    const showModal = ref(false)
    const editingId = ref(null)
    const deletingId = ref(null)
    const formError = ref('')
    const form = ref({ name: '', group_id: '', contact_ids: [], departure_region_ids: [], is_fallback: false, is_active: true })

    const accountsByContactId = computed(() => {
      const result = new Map()
      for (const account of accounts.value) {
        const contactId = Number(account.contact_id)
        if (Number.isFinite(contactId) && contactId > 0 && !result.has(contactId)) {
          result.set(contactId, account)
        }
      }
      return result
    })

    const displayAccountName = (account) => {
      return String(account?.manager_name || account?.employee_name || account?.employee_email || `Контакт ${account?.contact_id}`).trim()
    }

    const accountByContactId = (contactId) => accountsByContactId.value.get(Number(contactId))

    const accountDisplayName = (contactId) => {
      const account = accountByContactId(contactId)
      return account ? displayAccountName(account) : `Контакт ${contactId}`
    }

    const accountDetails = (contactId) => {
      const account = accountByContactId(contactId)
      if (!account) return `contact_id: ${contactId} · пользователь не найден в ATI`
      return `${account.employee_email || 'без почты'} · contact_id: ${contactId}${account.is_active ? '' : ' · выключен'}`
    }

    const accountOptions = computed(() => {
      return [...accountsByContactId.value.values()].sort((left, right) => {
        if (left.is_active !== right.is_active) return left.is_active ? -1 : 1
        return displayAccountName(left).localeCompare(displayAccountName(right), 'ru')
      }).map((account) => ({
        id: Number(account.contact_id),
        label: displayAccountName(account),
        details: `${account.employee_email || 'без почты'} · contact ${account.contact_id}`,
        inactive: !account.is_active,
        status: account.is_active ? 'активен' : 'выключен',
      }))
    })

    const regionsById = computed(() => new Map(regions.value.map((region) => [Number(region.region_id), region])))
    const regionDisplayName = (regionId) => regionsById.value.get(Number(regionId))?.region_name || `Регион ${regionId}`
    const regionOptions = computed(() => regions.value.map((region) => ({
      id: Number(region.region_id),
      label: region.region_name,
      details: `${region.country_name || 'страна не указана'} · region ${region.region_id}`,
    })))

    const existingSlugs = computed(() => items.value.map((g) => g.group_id))

    const makeUniqueSlug = (base) => {
      const cleaned = String(base || '').trim()
      if (!cleaned) return ''
      const taken = new Set(existingSlugs.value)
      if (!taken.has(cleaned)) return cleaned
      let slug = `${cleaned}-${noiseToken()}`
      while (taken.has(slug)) slug = `${cleaned}-${noiseToken()}`
      return slug
    }

    watch(() => form.value.name, (name) => {
      if (editingId.value) return
      const base = translitSlug(name)
      form.value.group_id = base ? makeUniqueSlug(base) : ''
    })

    const filteredItems = computed(() => {
      const q = search.value.trim().toLowerCase()
      if (!q) return items.value
      return items.value.filter(
        (g) =>
          g.group_id.toLowerCase().includes(q) ||
          (g.name || '').toLowerCase().includes(q),
      )
    })

    const loadGroups = async () => {
      loading.value = true
      accountsLoading.value = true
      regionsLoading.value = true
      try {
        const [groupsData, accountsData] = await Promise.all([
          getGroups({ active_only: false }),
          getAccounts({ active_only: false }).catch(() => null),
        ])
        items.value = Array.isArray(groupsData) ? groupsData : []
        if (accountsData === null) {
          accountsError.value = true
        } else {
          accounts.value = Array.isArray(accountsData) ? accountsData : []
          accountsError.value = false
        }
        hasError.value = false
      } catch {
        hasError.value = true
      } finally {
        loading.value = false
        accountsLoading.value = false
      }
      try {
        const regionsData = await searchRegions('', 100, 'Россия')
        regions.value = Array.isArray(regionsData) ? regionsData : []
        regionsError.value = false
      } catch {
        regionsError.value = true
      } finally {
        regionsLoading.value = false
      }
    }

    const onRegionsSearch = async (q) => {
      regionsLoading.value = true
      try {
        const regionsData = await searchRegions(q, 100, 'Россия')
        if (Array.isArray(regionsData)) {
          const resultIds = new Set(regionsData.map((r) => Number(r.region_id)))
          const selectedKept = regions.value.filter(
            (r) => form.value.departure_region_ids.includes(Number(r.region_id))
              && !resultIds.has(Number(r.region_id)),
          )
          regions.value = [...regionsData, ...selectedKept]
          regionsError.value = false
        }
      } catch {
        regionsError.value = true
      } finally {
        regionsLoading.value = false
      }
    }

    const startCreate = () => {
      editingId.value = null
      form.value = { name: '', group_id: '', contact_ids: [], departure_region_ids: [], is_fallback: false, is_active: true }
      formError.value = ''
      showModal.value = true
    }

    const startEdit = (item) => {
      editingId.value = item.group_id
      form.value = {
        name: item.name,
        group_id: item.group_id,
        contact_ids: [...new Set((item.contact_ids || []).map(Number).filter((id) => Number.isFinite(id) && id > 0))],
        departure_region_ids: [...new Set((item.departure_region_ids || []).map(Number).filter((id) => Number.isFinite(id) && id > 0))],
        is_fallback: item.is_fallback,
        is_active: item.is_active,
      }
      formError.value = ''
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      editingId.value = null
      formError.value = ''
    }

    const validate = () => {
      if (!String(form.value.name || '').trim()) return 'Заполните название'
      if (!String(form.value.group_id || '').trim()) return 'Заполните ID (slug)'
      return ''
    }

    const save = async () => {
      formError.value = validate()
      if (formError.value) return
      const payload = {
        group_id: form.value.group_id.trim(),
        name: form.value.name.trim(),
        contact_ids: [...new Set(form.value.contact_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0))],
        departure_region_ids: [...new Set(form.value.departure_region_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0))],
        is_fallback: form.value.is_fallback,
        is_active: form.value.is_active,
      }
      saving.value = true
      try {
        const saved = await upsertGroup(payload)
        const idx = items.value.findIndex((g) => g.group_id === saved.group_id)
        if (idx !== -1) items.value.splice(idx, 1, saved)
        else items.value.unshift(saved)
        closeModal()
      } catch (error) {
        formError.value = extractError(error, 'Не удалось сохранить группу')
      } finally {
        saving.value = false
      }
    }

    const removeGroup = async (item) => {
      if (!window.confirm(`Удалить группу «${item.name || item.group_id}»?`)) return
      deletingId.value = item.group_id
      try {
        await deleteGroup(item.group_id)
        items.value = items.value.filter((g) => g.group_id !== item.group_id)
      } catch {
        hasError.value = true
      } finally {
        deletingId.value = null
      }
    }

    const extractError = (error, fallback) => {
      if (typeof error.response?.data?.detail === 'string') return error.response.data.detail
      if (typeof error.response?.data?.detail === 'object' && error.response.data.detail) {
        return JSON.stringify(error.response.data.detail)
      }
      return fallback
    }

    onMounted(loadGroups)

    const dupSource = ref(null)
    const dupName = ref('')
    const dupSlug = ref('')
    const dupIsFallback = ref(false)
    const dupIsActive = ref(true)
    const duplicating = ref(false)
    const dupResult = ref('')
    const dupError = ref('')

    watch(dupName, (name) => {
      const base = translitSlug(name)
      dupSlug.value = base ? makeUniqueSlug(base) : ''
    })

    const openDuplicate = (item) => {
      dupSource.value = item
      dupName.value = `${item.name || ''} (копия)`.trim()
      const base = translitSlug(item.name)
      dupSlug.value = base ? makeUniqueSlug(base) : ''
      dupIsFallback.value = !!item.is_fallback
      dupIsActive.value = item.is_active !== false
      dupResult.value = ''
      dupError.value = ''
    }

    const closeDuplicate = () => {
      dupSource.value = null
      dupName.value = ''
      dupSlug.value = ''
      dupIsFallback.value = false
      dupIsActive.value = true
      dupResult.value = ''
      dupError.value = ''
    }

    const confirmDuplicate = async () => {
      const src = dupSource.value
      if (!src) return
      const name = String(dupName.value || '').trim()
      const slug = makeUniqueSlug(String(dupSlug.value || '').trim()) || String(dupSlug.value || '').trim()
      if (!name) { dupError.value = 'Заполните название'; return }
      if (!slug) { dupError.value = 'Заполните ID (slug)'; return }
      duplicating.value = true
      dupError.value = ''
      dupResult.value = ''
      try {
        const newGroup = await upsertGroup({
          group_id: slug,
          name,
          contact_ids: src.contact_ids || [],
          departure_region_ids: src.departure_region_ids || [],
          is_fallback: dupIsFallback.value,
          is_active: dupIsActive.value,
        })
        const all = await getBindings({ active_only: false })
        const sourceBindings = (Array.isArray(all) ? all : []).filter(
          (b) => b.scope === 'group' && b.group_id === src.group_id,
        )
        let created = 0
        let skipped = 0
        for (const b of sourceBindings) {
          const payload = {
            binding_id: `b-${b.from_city_id}-${b.to_city_id}-${slug}`,
            from_city_id: b.from_city_id,
            to_city_id: b.to_city_id,
            scope: 'group',
            group_id: slug,
            contact_id: null,
            priority: 0,
            is_active: b.is_active ?? true,
          }
          try {
            await upsertBinding(payload)
            created += 1
          } catch {
            skipped += 1
          }
        }
        const idx = items.value.findIndex((g) => g.group_id === newGroup.group_id)
        if (idx !== -1) items.value.splice(idx, 1, newGroup)
        else items.value.unshift(newGroup)
        dupResult.value = `Создана группа «${newGroup.name}» (${newGroup.group_id}). Скопировано привязок: ${created}` + (skipped ? `, пропущено: ${skipped}` : '') + `.`
        dupSource.value = null
      } catch (error) {
        dupError.value = extractError(error, 'Не удалось создать группу')
      } finally {
        duplicating.value = false
      }
    }

    return {
      items, filteredItems, loading, saving, hasError, search,
      accountsLoading, accountsError, accountOptions,
      regionsLoading, regionsError, regionOptions, regionDisplayName,
      showModal, editingId, deletingId, formError, form,
      loadGroups, startCreate, startEdit, closeModal, save, removeGroup,
      onRegionsSearch,
      displayAccountName, accountByContactId, accountDisplayName, accountDetails,
      dupSource, dupName, dupSlug, dupIsFallback, dupIsActive, duplicating, dupResult, dupError,
      openDuplicate, closeDuplicate, confirmDuplicate,
    }
  },
}
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.toolbar-actions { display: flex; gap: 8px; }
.search-input { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; box-sizing: border-box; font-size: 13px; height: 36px; padding: 8px 10px; width: 260px; }
.table-wrap { overflow-x: auto; }
.data-table { border-collapse: collapse; width: 100%; font-size: 13px; min-width: 700px; }
.data-table th, .data-table td { border-bottom: 1px solid #edf0f3; padding: 8px 10px; text-align: left; vertical-align: middle; }
.data-table th { background: #f8fafc; color: #475569; font-size: 11px; font-weight: 800; white-space: nowrap; }
.data-table tbody tr:hover { background: #eef4ff; }
.mono { font-family: ui-monospace, monospace; font-size: 12px; }
.col-bool { text-align: center; }
.data-table th.col-actions, .data-table td.col-actions { text-align: right; white-space: nowrap; }
.empty-cell { text-align: center !important; color: #6b7280; padding: 24px !important; }
.badge { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.badge-on { background: #e6f4ea; color: #1e7e34; }
.badge-off { background: #f1f5f9; color: #6b7280; }
.error-message { background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; color: #991b1b; font-size: 12px; margin-bottom: 12px; padding: 10px 12px; }
.info-message { background: #eff6ff; border: 1px solid #dbeafe; border-radius: 10px; color: #1d4ed8; font-size: 12px; margin: 10px 0; padding: 10px 12px; }
.modal-hint { color: #475569; font-size: 13px; margin: 0 0 14px; line-height: 1.5; }
button { border: none; border-radius: 8px; box-sizing: border-box; cursor: pointer; font-weight: 700; font-size: 12px; height: 36px; padding: 0 14px; }
button:disabled { cursor: not-allowed; opacity: 0.5; }
.btn-primary { background: #2563eb; color: #fff; }
.btn-secondary { background: #f1f5f9; color: #475569; }
.col-actions button { height: 30px; padding: 0 10px; }
.col-actions button + button { margin-left: 6px; }
.btn-edit { background: #eef4ff; color: #1d4ed8; }
.btn-copy { background: #f0fdf4; color: #166534; }
.btn-save { background: #16a34a; color: #fff; }
.btn-cancel { background: #f1f5f9; color: #475569; }
.btn-delete { background: #fee2e2; color: #991b1b; }
.modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #fff; border-radius: 14px; box-shadow: 0 20px 50px rgba(15,23,42,0.25); max-height: 92vh; overflow-y: auto; padding: 22px; width: 900px; max-width: 94vw; }
.modal h2 { margin: 0 0 14px; font-size: 18px; }
.field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 12px; color: #475569; }
.field-inline { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 13px; color: #1f2937; }
.modal-input { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; padding: 8px 10px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.members-inline { display: flex; flex-wrap: wrap; gap: 4px; }
.member-inline { background: #eef4ff; border-radius: 999px; color: #1d4ed8; display: inline-block; font-size: 11px; font-weight: 600; max-width: 190px; overflow: hidden; padding: 3px 8px; text-overflow: ellipsis; white-space: nowrap; }
.member-inactive { background: #f1f5f9; color: #6b7280; text-decoration: line-through; }
.region-inline { background: #f0fdf4; border-radius: 999px; color: #166534; display: inline-block; font-size: 11px; font-weight: 600; max-width: 180px; overflow: hidden; padding: 3px 8px; text-overflow: ellipsis; white-space: nowrap; }
.region-more { color: #64748b; font-size: 11px; padding: 3px 4px; }
.group-editor-grid { display: grid; gap: 12px; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); margin-bottom: 12px; }
.member-picker { border: 1px solid #d1d5db; border-radius: 10px; overflow: hidden; }
.selected-members { display: flex; flex-wrap: wrap; gap: 6px; padding: 10px; border-bottom: 1px solid #e5e7eb; }
.selected-member { align-items: center; background: #eef4ff; border: 1px solid #dbeafe; border-radius: 8px; display: flex; gap: 8px; max-width: 100%; padding: 6px 8px; }
.selected-member-inactive { background: #f8fafc; border-color: #e5e7eb; }
.selected-member-text { display: flex; flex-direction: column; min-width: 0; }
.selected-member-text strong { color: #1f2937; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-member-text span { color: #6b7280; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.member-remove { align-items: center; background: transparent; color: #64748b; display: inline-flex; flex: 0 0 auto; font-size: 18px; height: 24px; justify-content: center; padding: 0; width: 24px; }
.member-search { border: 0; border-bottom: 1px solid #e5e7eb; box-sizing: border-box; font-size: 13px; outline: none; padding: 10px 12px; width: 100%; }
.member-options { max-height: 250px; overflow-y: auto; padding: 4px; }
.member-select-all { align-items: center; background: #f8fafc; border-bottom: 1px solid #e5e7eb; cursor: pointer; display: flex; gap: 9px; margin: -4px -4px 4px; padding: 9px 12px; position: sticky; top: -4px; z-index: 1; }
.member-select-all span { color: #1f2937; display: flex; flex: 1; font-size: 12px; font-weight: 700; justify-content: space-between; }
.member-select-all small { color: #64748b; font-size: 10px; font-weight: 500; }
.member-select-all-disabled { cursor: default; opacity: 0.6; }
.member-option { align-items: center; border-radius: 7px; cursor: pointer; display: flex; gap: 9px; padding: 7px 8px; }
.member-option:hover { background: #f1f5f9; }
.member-option-inactive { opacity: 0.72; }
.member-option-main { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.member-option-main strong { color: #1f2937; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.member-option-main small { color: #6b7280; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-state { border-radius: 999px; flex: 0 0 auto; font-size: 10px; font-weight: 700; padding: 2px 7px; }
.account-active { background: #e6f4ea; color: #1e7e34; }
.account-inactive { background: #f1f5f9; color: #6b7280; }
.member-empty, .member-warning { color: #6b7280; font-size: 12px; padding: 12px; text-align: center; }
.member-warning { background: #fff7ed; color: #9a3412; }
@media (max-width: 760px) {
  .group-editor-grid { grid-template-columns: 1fr; }
}
</style>
